import * as Sentry from "@sentry/react";

import {
  getCollectionOfDittoComponents,
  useTexts,
} from "Components/TextContext";
import {
  formBooleanToBoolean,
  mapValidationErrorsDitto,
} from "Utils/formUtils";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { StyledAlert } from "App/global-style";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { ProductProps } from "Components/Features/CheckPrice/Products/productInterfaces";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { Select } from "Components/Form/Select";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getError } from "Utils/productUtils";
import React from "react";
import { FindAddress } from "../../FormComponentsHouseProducts/FindAddress";
import { HuseierneHouseContentsProductIntroFormValues } from "../huseierneHouseContentsFormValuesInterfaces";
import { HUSEIERNE_HOUSE_CONTENTS_INTRO_STEP } from "../huseierneHouseContentsSteps";
import { getCoverageAmountOptions } from "./huseierneHouseContentsProductIntroUtils";
import { validationSchema } from "./huseierneHouseContentsProductIntroValidation";

export const HuseierneHouseContentsProductIntro = ({
  updatePriceRequest,
}: ProductProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { getText, createLinkComponent } = useTexts();

  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const initialFormState =
    useFormState<HuseierneHouseContentsProductIntroFormValues>(
      HUSEIERNE_HOUSE_CONTENTS_INTRO_STEP.id,
      initialFormValues,
    );

  const { shorttitle: shortTitleComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.HUSEIERNE_HOUSE_CONTENTS,
  );

  const handleSubmit = (
    form: HuseierneHouseContentsProductIntroFormValues,
  ): void => {
    dispatch(
      updatePriceCalculationFormState({
        productName: INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS,
        stepId: HUSEIERNE_HOUSE_CONTENTS_INTRO_STEP.id,
        formState: form,
      }),
    );
    updatePriceRequest({
      productPriceUrl: BackendProductInfo.HUSEIERNE_HOUSE_CONTENTS.priceReqUrl,
      stepForwardOnSuccess: true,
      updatedProductPriceRequestInfo: {
        addressSameAsHomeAddress: formBooleanToBoolean(
          form.addressSameAsHomeAddress,
        ),
        ...(form.addressSameAsHomeAddress !== BooleanFormType.TRUE && {
          postNumber: form.postNumber,
        }),
        coverageAmount: form.coverageAmount,
      },
    });
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS}`,
        );
      }}
    >
      <ProductIntroTile
        productBackendName={ProductBackendName.HUSEIERNE_HOUSE_CONTENTS}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(
          formProps: FormikProps<HuseierneHouseContentsProductIntroFormValues>,
        ) => {
          return (
            <Form>
              <FindAddress
                product={ProductBackendName.HUSEIERNE_HOUSE_CONTENTS}
                formProps={formProps}
                label={getText("huseiernehousecontentsform.label.postnumber")}
              />
              {formProps.values.addressSameAsHomeAddress ===
                BooleanFormType.FALSE && (
                <StyledAlert title="" type="info" fluid>
                  <p>
                    {createLinkComponent(
                      "partnerhuseierne.link.notsameasregisteredaddresstext",
                    )}
                  </p>
                </StyledAlert>
              )}
              <Select
                name="coverageAmount"
                id="coverageAmount"
                label={getText(
                  "huseiernehousecontentsform.label.coverageamount",
                )}
                error={getError("coverageAmount", formProps)}
                options={getCoverageAmountOptions(
                  getText(
                    "huseiernehousecontentsform.options.coverageamount-defaultlabel",
                  ),
                )}
                numeric
                large
              />
              <PartnerProductIntroForms />
              <MarketingConsent /> {/*TODO ??? UNION MEMBERSHIP */}
              {formProps.submitCount > 0 &&
                Object.keys(formProps.errors).length > 0 && (
                  <ErrorsSummedUp
                    errors={mapValidationErrorsDitto(
                      formProps.errors,
                      shortTitleComponents,
                    )}
                    isVisible
                  />
                )}
              <ScrollToFirstError />
              <NavigationButtons
                isLoading={priceCalculationResult.isLoading}
                hidePreviousButton
              />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.HUSEIERNE_HOUSE_CONTENTS}
                stepId={HUSEIERNE_HOUSE_CONTENTS_INTRO_STEP.id}
              />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};

const initialFormValues = {
  addressSameAsHomeAddress: undefined,
  postNumber: undefined,
  city: undefined,
  validPostNumber: undefined,
  coverageAmount: undefined,
};
